export const BODY_SURFACE_GLSL = `    const float diskRadius = 0.415;
    float disk = 1.0 - smoothstep(0.398, diskRadius, radius);
    float atmosphereShell = 1.0 - smoothstep(diskRadius, 0.50, radius);
    float halo = max(0.0, atmosphereShell - disk);
    // Instanced quad coordinates already use the world-space positive-Y basis.
    vec2 physicalPoint = centered;
    vec2 spherePosition = physicalPoint / diskRadius;
    float sphereRadiusSquared = dot(spherePosition, spherePosition);
    float sphereZ = sqrt(max(0.0, 1.0 - sphereRadiusSquared));
    vec3 normal = normalize(vec3(spherePosition, sphereZ));
    vec2 starVector = u_starPosition - v_position;
    vec3 lightDirection = vec3(
      starVector / max(length(starVector), 0.0001),
      0.0
    );
    float rawLight = dot(normal, lightDirection);
    float diffuse = pow(max(0.0, rawLight), 0.72);
    float twilight = smoothstep(-0.08, 0.14, rawLight);
    vec3 spinAxis = planetSpinAxis(v_bodyIndex);
    float spinAngle = planetSpinAngle(v_bodyIndex);
    vec3 bodyNormal = toBodyNormal(normal, spinAxis, spinAngle);
    float orbitAngle = atan(v_position.y - u_starPosition.y, v_position.x - u_starPosition.x);
    float season = sin(orbitAngle + bodySeasonPhase(v_bodyIndex));

    float vegetationCoordinate = surfaceVegetationCoordinate(spherePosition, spinAngle);
    int vegetationCell = clamp(
      int(floor(vegetationCoordinate)),
      0,
      31
    );
    int vegetationOffset =
      (clamp(int(v_bodyIndex), 1, 16) - 1) * 32;
    float vegetationBlend = smoothstep(0.0, 1.0, fract(vegetationCoordinate));
    int nextVegetationCell = (vegetationCell + 1) % 32;
    float algaeSignal = mix(
      u_planetVegetation[vegetationOffset + vegetationCell],
      u_planetVegetation[vegetationOffset + nextVegetationCell],
      vegetationBlend
    );
    int leftVegetationCell = (vegetationCell + 31) % 32;
    int rightVegetationCell = (vegetationCell + 1) % 32;
    float neighboringAlgae =
      (
        u_planetVegetation[vegetationOffset + leftVegetationCell] +
        u_planetVegetation[vegetationOffset + rightVegetationCell]
      ) * 0.5;
    float grazingScar =
      smoothstep(0.025, 0.12, neighboringAlgae - algaeSignal) *
      (1.0 - smoothstep(0.42, 0.68, algaeSignal));
    float liquidMask;
    float biosphere;
    vec3 albedo = proceduralBodyAlbedo(
      bodyNormal,
      v_bodyMaterial,
      v_bodyLowColor,
      v_bodyHighColor,
      v_bodyLiquidColor,
      algaeSignal,
      season,
      liquidMask,
      biosphere
    );
    // Cloud amount is supplied by condensation/precipitation and volatile
    // chemistry. Advection uses the same simulation clock as the surface.
    float cloudNoise = fbm3D(bodyNormal * 6.4 + vec3(
      u_simulationTime * 0.012, 0.0, -u_simulationTime * 0.007
    ));
    float clouds = smoothstep(
      0.84 - v_bodyClimate.x * 0.66,
      0.94 - v_bodyClimate.x * 0.66,
      cloudNoise
    ) * v_bodyClimate.x;
    albedo = mix(albedo, v_bodyCloudColour, clouds * 0.88);
    albedo *= 0.72 + v_bodyClimate.z * 0.9;
    albedo = mix(
      albedo,
      vec3(0.58, 0.15, 0.035),
      grazingScar * (1.0 - liquidMask) * 0.62
    );
    vec3 atmosphereColor = mix(
      v_bodyAtmosphereColor.rgb,
      vec3(0.12, 0.58, 0.92),
      biosphere * 0.24
    );
    float atmosphereStrength = v_bodyMaterial.y;
    float ambientLight = 0.024 + atmosphereStrength * 0.022;

    float limb = pow(max(0.0, 1.0 - sphereZ), 2.2);
    vec2 rimDirection = physicalPoint / max(radius, 0.0001);
    float haloLight = smoothstep(
      -0.42,
      0.58,
      dot(rimDirection, lightDirection.xy)
    );
    vec3 surface = albedo * (ambientLight + diffuse * 1.08);
    surface +=
      v_bodyLiquidColor.rgb *
      liquidMask *
      pow(max(0.0, diffuse), 7.0) *
      0.42;
    surface +=
      atmosphereColor *
      limb *
      atmosphereStrength *
      (0.035 + twilight * 0.24);
    vec3 haloColor =
      atmosphereColor *
      atmosphereStrength *
      (0.18 + haloLight * 0.74);
    color = mix(haloColor, surface, disk);
    int planetSlot = clamp(int(v_bodyIndex) - 1, 0, 15);
    vec2 surfacePosition =
      v_position + spherePosition * u_planetRadii[planetSlot];
    color *= mix(
      0.055,
      1.0,
      stellarVisibility(surfacePosition, v_bodyIndex)
    );
    // Ionized material emits its own light, including on the stellar night side.
    color += v_bodyAtmosphereColor.rgb * v_bodyLiquidColor.a * disk * 1.5;
    alpha = max(
      disk * 0.99,
      halo * atmosphereStrength * (0.10 + haloLight * 0.30)
    );
`;
